import { useState, useEffect, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { baseUrl } from "../../config/baseUrl"
import axios from "axios"
import toastify from "../../config/toastify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft, faBoxOpen, faSave, faUpload, faTimes, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import JoditEditor from "jodit-react";
import editorConfig from "../../utils/editorConfig";

export default function NewPackageTesting() {
    const [formval, setFormval] = useState({
        title: "",
        description: "",
        status: "active",
        subcategoryID: "",
        categoryID: "",
    })

    // Validity options
    const VALIDITY_OPTIONS = [
        { value: "3", label: "3 Months", days: 90 },
        { value: "6", label: "6 Months", days: 180 },
        { value: "12", label: "12 Months", days: 365 },
    ];

    // Dynamic pricing plans array — initially 1 card with 3 Months
    const [pricingPlans, setPricingPlans] = useState([
        { id: 1, validity: "3", price: "", offerPrice: "", discountPercent: "", isPopular: false },
    ]);

    const [selectedFile, setSelectedFile] = useState(null)
    const [previewUrl, setPreviewUrl] = useState(null)
    const [isDragging, setIsDragging] = useState(false)
    const [loading, setLoading] = useState(false)
    const [categories, setCategories] = useState([])
    const [subcategories, setSubcategories] = useState([])

    const navigate = useNavigate()

    // Which validity values are already used
    const usedValidities = pricingPlans.map(p => p.validity);

    // Get first available validity not yet used
    const getNextAvailableValidity = () => {
        return VALIDITY_OPTIONS.find(opt => !usedValidities.includes(opt.value))?.value || "";
    };

    const addPricingPlan = () => {
        if (pricingPlans.length >= 3) return;
        const nextVal = getNextAvailableValidity();
        if (!nextVal) return;
        setPricingPlans(prev => [
            ...prev,
            { id: Date.now(), validity: nextVal, price: "", offerPrice: "", discountPercent: "", isPopular: false },
        ]);
    };

    const handlePopularChange = (id, checked) => {
        setPricingPlans(prev => prev.map(plan => {
            if (plan.id === id) {
                return { ...plan, isPopular: checked };
            }
            return checked ? { ...plan, isPopular: false } : plan;
        }));
    };

    const removePricingPlan = (id) => {
        if (pricingPlans.length <= 1) {
            return toastify.error("At least one pricing plan is required");
        }
        setPricingPlans(prev => prev.filter(p => p.id !== id));
    };

    const updatePlanField = (id, field, value) => {
        setPricingPlans(prev => prev.map(plan => {
            if (plan.id !== id) return plan;
            const updated = { ...plan, [field]: value };

            // Auto-calculate
            if (field === "price") {
                const p = parseFloat(value) || 0;
                if (p && updated.discountPercent !== "") {
                    const disc = parseFloat(updated.discountPercent) || 0;
                    updated.offerPrice = Math.round(p * (1 - disc / 100)).toString();
                } else if (p && updated.offerPrice) {
                    const off = parseFloat(updated.offerPrice) || 0;
                    if (off <= p) {
                        updated.discountPercent = Math.round(((p - off) / p) * 100).toString();
                    }
                }
            } else if (field === "offerPrice") {
                const off = parseFloat(value) || 0;
                const p = parseFloat(updated.price) || 0;
                if (p && off <= p) {
                    updated.discountPercent = Math.round(((p - off) / p) * 100).toString();
                } else if (p) {
                    updated.discountPercent = "0";
                }
            } else if (field === "discountPercent") {
                const disc = parseFloat(value) || 0;
                const p = parseFloat(updated.price) || 0;
                if (p && value !== "") {
                    updated.offerPrice = Math.round(p * (1 - disc / 100)).toString();
                }
            }

            return updated;
        }));
    };

    const getValidityLabel = (val) => {
        return VALIDITY_OPTIONS.find(o => o.value === val)?.label || val;
    };

    // --- Standard Form Handlers ---
    const changeHandler = (e) => {
        const { name, value } = e.target
        setFormval((prevVal) => ({
            ...prevVal,
            [name]: value,
        }))
    }

    const handleFileChange = (e) => {
        const file = e.target.files[0]
        if (file) {
            setSelectedFile(file)
            const reader = new FileReader()
            reader.onload = () => {
                setPreviewUrl(reader.result)
            }
            reader.readAsDataURL(file)
        }
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        setIsDragging(true)
    }

    const handleDragLeave = () => {
        setIsDragging(false)
    }

    const handleDrop = (e) => {
        e.preventDefault()
        setIsDragging(false)
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0]
            setSelectedFile(file)
            const reader = new FileReader()
            reader.onload = () => {
                setPreviewUrl(reader.result)
            }
            reader.readAsDataURL(file)
        }
    }

    const removeFile = () => {
        setSelectedFile(null)
        setPreviewUrl(null)
    }

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${baseUrl}/category`, {
                headers: { Authorization: localStorage.getItem("token") },
            })
            setCategories(response.data.data || [])
        } catch (error) {
            console.error("Error fetching categories:", error)
        }
    }

    const fetchSubcategories = async () => {
        try {
            const response = await axios.get(`${baseUrl}/subcategory`, {
                headers: { Authorization: localStorage.getItem("token") },
            })
            setSubcategories(response.data.data || [])
        } catch (error) {
            console.error("Error fetching subcategories:", error)
        }
    }

    useEffect(() => {
        fetchSubcategories()
        fetchCategories()
    }, [])

    const filteredSubCat = formval.categoryID
        ? subcategories.filter((subcat) => subcat?.categoryId?._id === formval.categoryID)
        : subcategories;

    const config = useMemo(() => ({
        ...editorConfig,
        height: 500,
        placeholder: "Enter package description",
    }), []);

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!formval.title) return toastify.error("Title is required")
        if (!formval.description) return toastify.error("Description is required")
        if (!formval.categoryID) return toastify.error("Category is required")
        if (!formval.subcategoryID) return toastify.error("Subcategory is required")
        if (!formval.status) return toastify.error("Status is required")

        // Validate pricing plans
        for (let i = 0; i < pricingPlans.length; i++) {
            if (!pricingPlans[i].price || Number(pricingPlans[i].price) <= 0) {
                return toastify.error(`${getValidityLabel(pricingPlans[i].validity)} plan: Price is required`);
            }

            if (
                pricingPlans[i].offerPrice &&
                Number(pricingPlans[i].offerPrice) > Number(pricingPlans[i].price)
            ) {
                return toastify.error(`${getValidityLabel(pricingPlans[i].validity)} plan: Offer price cannot be greater than regular price`);
            }
        }

        setLoading(true)

        try {
            const planPayload = pricingPlans.map(plan => ({
                validity: Number(plan.validity),
                regularPrice: Number(plan.price),
                offerPrice: plan.offerPrice ? Number(plan.offerPrice) : Number(plan.price),
                is_popular: !!plan.isPopular
            }));

            const formData = new FormData()
            formData.append("title", formval.title)
            formData.append("description", formval.description)
            formData.append("subcategoryID", formval.subcategoryID)
            formData.append("status", formval.status)
            formData.append("plan", JSON.stringify(planPayload))

            if (selectedFile) {
                formData.append("image", selectedFile)
            }

            const response = await axios.post(`${baseUrl}/package`, formData, {
                headers: {
                    Authorization: localStorage.getItem("token"),
                }
            })

            if (!response?.data?.status) {
                toastify.error("An error occurred while creating package")
                return
            }

            toastify.success("Package created successfully")
            navigate("/test-packages")
        } catch (error) {
            console.error("Error creating package:", error)
            toastify.error(error.response?.data?.message || "Failed to create package")
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <style>{`
                .main-sec {
                    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                    min-height: 100vh;
                    padding: 2rem 0;
                }
                .dashboard-title { margin-bottom: 1.5rem; }
                .dash-head { color: #333; font-size: 1.8rem; font-weight: 700; margin: 0; }
                .cards {
                    background: white;
                    border-radius: 15px;
                    padding: 2rem;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
                    margin-bottom: 2rem;
                }
                .form-section {
                    margin-bottom: 2rem;
                    border-bottom: 1px solid #eee;
                    padding-bottom: 1.5rem;
                }
                .form-section:last-child {
                    border-bottom: none;
                    margin-bottom: 0;
                    padding-bottom: 0;
                }
                .section-title {
                    color: #7436A9;
                    font-weight: 650;
                    margin-bottom: 1.5rem;
                    font-size: 1.2rem;
                }

                /* Pricing Plan Card */
                .pricing-plan-card {
                    background: #f8f9ff;
                    border: 2px solid #e8e8f8;
                    border-radius: 12px;
                    padding: 1.25rem;
                    margin-bottom: 1rem;
                    position: relative;
                    transition: all 0.3s ease;
                }
                .pricing-plan-card:hover {
                    border-color: #7436A9;
                    box-shadow: 0 4px 15px rgba(116, 54, 169, 0.1);
                }
                .plan-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1rem;
                }
                .plan-badge {
                    background: linear-gradient(135deg, #7436A9 0%, #764ba2 100%);
                    color: white;
                    padding: 0.3rem 0.8rem;
                    border-radius: 20px;
                    font-size: 0.85rem;
                    font-weight: 600;
                }
                .plan-remove-btn {
                    background: #ef5350;
                    color: white;
                    border: none;
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s ease;
                    font-size: 0.8rem;
                }
                .plan-remove-btn:hover {
                    background: #c62828;
                    transform: scale(1.1);
                }
                .add-plan-btn {
                    background: transparent;
                    color: #7436A9;
                    border: 2px dashed #7436A9;
                    border-radius: 8px;
                    padding: 0.5rem 1.2rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    width: auto;
                    font-size: 0.85rem;
                }
                .add-plan-btn:hover {
                    background: rgba(116, 54, 169, 0.08);
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(116, 54, 169, 0.15);
                }
                .discount-badge {
                    display: inline-block;
                    background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
                    color: white;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.8rem;
                    font-weight: 700;
                    margin-left: 0.5rem;
                    vertical-align: middle;
                }

                .file-upload-container { margin-bottom: 1.5rem; }
                .file-upload-area {
                    border: 2px dashed #ddd;
                    border-radius: 10px;
                    padding: 2rem;
                    text-align: center;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                }
                .file-upload-area.dragging {
                    border-color: #7436A9;
                    background: rgba(102, 126, 234, 0.1);
                }
                .file-upload-area:hover { border-color: #7436A9; }
                .file-upload-icon { font-size: 3rem; color: #7436A9; margin-bottom: 1rem; }
                .file-upload-text { font-size: 1.1rem; font-weight: 600; color: #333; margin-bottom: 0.5rem; }
                .file-upload-subtext { color: #666; font-size: 0.9rem; }
                .file-upload-input {
                    position: absolute; top: 0; left: 0;
                    width: 100%; height: 100%; opacity: 0; cursor: pointer;
                }
                .file-preview-container { position: relative; display: inline-block; }
                .file-preview {
                    max-width: 200px; max-height: 200px;
                    border-radius: 10px; box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
                }
                .file-remove-btn {
                    position: absolute; top: -10px; right: -10px;
                    background: #ef5350; color: white; border: none;
                    border-radius: 50%; width: 30px; height: 30px;
                    cursor: pointer; display: flex; align-items: center; justify-content: center;
                }
                .file-info { margin-top: 1rem; font-size: 0.9rem; color: #666; }
                .btn-for-add {
                    background: linear-gradient(135deg, #7436A9 0%, #764ba2 100%);
                    border: none; border-radius: 8px;
                    padding: 0.75rem 2rem; font-weight: 600;
                    transition: all 0.3s ease; color: white;
                }
                .btn-for-add:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
                }
                .btn-for-add:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
                .btn-info {
                    background: #4facfe; border: none; border-radius: 8px;
                    padding: 0.75rem 1.5rem; font-weight: 600; transition: all 0.3s ease;
                }
                .btn-info:hover {
                    background: #2f86fe; transform: translateY(-2px);
                    box-shadow: 0 10px 20px rgba(79, 172, 254, 0.3);
                }
                .loading-spinner {
                    width: 20px; height: 20px;
                    border: 2px solid #f3f3f3; border-top: 2px solid #7436A9;
                    border-radius: 50%; animation: spin 1s linear infinite;
                    margin-right: 0.5rem;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @media (max-width: 768px) {
                    .cards { padding: 1.5rem; }
                    .form-section { padding: 1rem; }
                }
            `}</style>

            <section className="main-sec">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <div className="dashboard-title">
                                <h4 className="dash-head">
                                    <FontAwesomeIcon icon={faBoxOpen} className="me-2" />
                                    New Package Testing (Admin)
                                </h4>
                            </div>
                            <div className="custom-bredcump">
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
                                        <li className="breadcrumb-item"><Link to="/test-packages">Packages</Link></li>
                                        <li className="breadcrumb-item active" aria-current="page">New Package Testing</li>
                                    </ol>
                                </nav>
                            </div>
                        </div>
                        <div className="col-lg-6 d-flex justify-content-end">
                            <Link to="/test-packages" className="btn btn-info text-white">
                                <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                                Back
                            </Link>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-12">
                            <div className="cards edit-usr">
                                <form onSubmit={handleSubmit}>
                                    {/* Package Basic Info */}
                                    <div className="form-section">
                                        <h5 className="section-title">Package Details</h5>
                                        <div className="row">
                                            <div className="col-lg-12 mb-4">
                                                <label htmlFor="title" className="form-label">
                                                    Title<sup className="text-danger">*</sup>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="title"
                                                    placeholder="Enter package title"
                                                    onChange={changeHandler}
                                                    value={formval?.title}
                                                />
                                            </div>
                                            <div className="col-lg-12 mb-4">
                                                <label htmlFor="description" className="form-label">
                                                    Description<sup className="text-danger">*</sup>
                                                </label>
                                                <JoditEditor
                                                    value={formval?.description || ""}
                                                    config={config}
                                                    onChange={(value) => {
                                                        let content = value;
                                                        const youtubeRegex =
                                                            /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{6,})[^\s]*/g;
                                                        content = content.replace(youtubeRegex, (match, videoId) => {
                                                            return `<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
                                                        });
                                                        setFormval(prev => ({ ...prev, description: content }));
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Dynamic Pricing Plans */}
                                    <div className="form-section">
                                        <h5 className="section-title">Pricing Plans</h5>

                                        {pricingPlans.map((plan, index) => (
                                            <div className="pricing-plan-card" key={plan.id}>
                                                <div className="plan-header">
                                                    <span className="plan-badge">
                                                        {getValidityLabel(plan.validity)}
                                                        {plan.discountPercent && Number(plan.discountPercent) > 0 && (
                                                            <span className="discount-badge">{plan.discountPercent}% OFF</span>
                                                        )}
                                                        {plan.isPopular && (
                                                            <span className="badge bg-warning text-dark ms-2" style={{ fontWeight: "700" }}>⭐ POPULAR</span>
                                                        )}
                                                    </span>
                                                    {pricingPlans.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="plan-remove-btn"
                                                            onClick={() => removePricingPlan(plan.id)}
                                                            title="Remove this plan"
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="row">
                                                    <div className="col-lg-3 mb-3">
                                                        <label className="form-label">Validity<sup className="text-danger">*</sup></label>
                                                        <select
                                                            className="form-control"
                                                            value={plan.validity}
                                                            onChange={(e) => updatePlanField(plan.id, "validity", e.target.value)}
                                                        >
                                                            {VALIDITY_OPTIONS.map(opt => (
                                                                <option
                                                                    key={opt.value}
                                                                    value={opt.value}
                                                                    disabled={usedValidities.includes(opt.value) && plan.validity !== opt.value}
                                                                >
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="col-lg-3 mb-3">
                                                        <label className="form-label">
                                                            Regular Price (₹)<sup className="text-danger">*</sup>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            placeholder="e.g. 2999"
                                                            value={plan.price}
                                                            onChange={(e) => updatePlanField(plan.id, "price", e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-lg-3 mb-3">
                                                        <label className="form-label">Offer Price (₹)</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            placeholder="e.g. 999"
                                                            value={plan.offerPrice}
                                                            onChange={(e) => updatePlanField(plan.id, "offerPrice", e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-lg-3 mb-3 d-flex align-items-center" style={{ paddingTop: "1.8rem" }}>
                                                        <div className="form-check form-switch">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`popular-${plan.id}`}
                                                                checked={plan.isPopular || false}
                                                                onChange={(e) => handlePopularChange(plan.id, e.target.checked)}
                                                                style={{ cursor: "pointer" }}
                                                            />
                                                            <label className="form-check-label ms-2" htmlFor={`popular-${plan.id}`} style={{ cursor: "pointer", fontWeight: "650", color: "#555" }}>
                                                                Popular Plan
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {pricingPlans.length < 3 && (
                                            <button type="button" className="add-plan-btn" onClick={addPricingPlan}>
                                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                Add Another Plan
                                            </button>
                                        )}
                                    </div>

                                    {/* Package Image */}
                                    <div className="form-section">
                                        <h5 className="section-title">Package Image</h5>
                                        <div className="file-upload-container">
                                            <div
                                                className={`file-upload-area ${isDragging ? "dragging" : ""}`}
                                                onDragOver={handleDragOver}
                                                onDragLeave={handleDragLeave}
                                                onDrop={handleDrop}
                                            >
                                                {!selectedFile ? (
                                                    <>
                                                        <div className="file-upload-icon">
                                                            <FontAwesomeIcon icon={faUpload} />
                                                        </div>
                                                        <div className="file-upload-text">Drag & Drop your image here</div>
                                                        <div className="file-upload-subtext">or click to browse files</div>
                                                        <input
                                                            type="file"
                                                            className="file-upload-input"
                                                            accept="image/*"
                                                            onChange={handleFileChange}
                                                        />
                                                    </>
                                                ) : (
                                                    <div className="file-preview-container">
                                                        <img src={previewUrl || "/placeholder.svg"} alt="Preview" className="file-preview" />
                                                        <button type="button" className="file-remove-btn" onClick={removeFile}>
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                        <div className="file-info">
                                                            {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Category Selection */}
                                    <div className="form-section">
                                        <h5 className="section-title">Category Selection</h5>
                                        <div className="row">
                                            <div className="col-lg-6 mb-4">
                                                <label htmlFor="categoryID" className="form-label">
                                                    Category<sup className="text-danger">*</sup>
                                                </label>
                                                <select
                                                    className="form-control"
                                                    name="categoryID"
                                                    onChange={changeHandler}
                                                    value={formval?.categoryID}
                                                >
                                                    <option value="">Select Category</option>
                                                    {categories.map((cat) => (
                                                        <option key={cat._id} value={cat._id}>{cat?.title}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-lg-6 mb-4">
                                                <label htmlFor="subcategoryID" className="form-label">
                                                    Subcategory<sup className="text-danger">*</sup>
                                                </label>
                                                <select
                                                    className="form-control"
                                                    name="subcategoryID"
                                                    onChange={changeHandler}
                                                    value={formval?.subcategoryID}
                                                >
                                                    <option value="">Select Subcategory</option>
                                                    {filteredSubCat.map((subcat) => (
                                                        <option key={subcat._id} value={subcat._id}>{subcat.title}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status */}
                                    <div className="form-section">
                                        <h5 className="section-title">Status</h5>
                                        <div className="row">
                                            <div className="col-lg-6 mb-4">
                                                <label htmlFor="status" className="form-label">
                                                    Status<sup className="text-danger">*</sup>
                                                </label>
                                                <select className="form-control" name="status" onChange={changeHandler} value={formval?.status}>
                                                    <option value="active">Active</option>
                                                    <option value="inactive">Inactive</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-lg-12 text-center mt-4">
                                        <button type="submit" className="btn btn-for-add text-white" disabled={loading}>
                                            {loading && <div className="loading-spinner"></div>}
                                            <FontAwesomeIcon icon={faSave} className="me-2" />
                                            {loading ? "Creating..." : "Create Package"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}
